/**
 * Fichiers de l'app disparus après un déploiement. Une page ouverte AVANT un déploiement réclame,
 * au premier chargement à la demande (moteur du brouillon, collection…), des fichiers dont le nom a
 * changé : le chargement échoue. Avant, la feuille retombait alors sur l'ancienne interface.
 *
 * `importWithRetry` réessaie (un réseau qui flanche) puis, si c'est bien un fichier disparu, recharge la
 * page UNE fois (jamais en boucle : pas deux rechargements en moins de 30 s). Les écrans qui reprennent
 * leur état (exercice, live) repartent où ils en étaient.
 */

const RELOAD_KEY = 'jr_chunk_reload';
const RELOAD_GUARD_MS = 30_000;

/** Le message d'un import dynamique dont le fichier n'existe plus. PUR. */
export function isChunkLoadError(err: unknown): boolean {
  const m = err instanceof Error ? `${err.name} ${err.message}` : String(err ?? '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|ChunkLoadError|Loading chunk .* failed|Unable to preload CSS/i.test(
    m,
  );
}

/** Recharge la page, sauf si on vient déjà de le faire. Renvoie vrai si la page se recharge. */
export function reloadOnceForNewVersion(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Charge un module à la demande ; réessaie, puis recharge la page si ses fichiers ont disparu. */
export async function importWithRetry<T>(
  load: () => Promise<T>,
  { delays = [600, 1800] }: { delays?: number[] } = {},
): Promise<T> {
  let last: unknown;
  for (let i = 0; i <= delays.length; i++) {
    try {
      return await load();
    } catch (e) {
      last = e;
      if (i < delays.length) await wait(delays[i]!);
    }
  }
  if (isChunkLoadError(last)) reloadOnceForNewVersion();
  throw last;
}
